import {useMemo} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {getMasterDataApi} from '../api/master-data-api';
import {masterDataKeys} from '../query-keys';
import type {ObjectRecord} from '../types/master-data-types';

/** Per-type record slice pulled for browse pickers. */
export const DEFAULT_RECORDS_PER_TYPE = 100;

/**
 * Batch-fetch a bounded slice of records for several object types in one
 * request, grouped by type id. Replaces a per-type `useQueries` fan-out:
 * one HTTP round-trip, one backend display-resolution pass.
 */
export function useObjectRecordsByType(
  typeIds: string[],
  perTypeLimit: number = DEFAULT_RECORDS_PER_TYPE,
  includeArchived = false
) {
  const tenantId = useRequiredTenantId();
  const api = getMasterDataApi();

  const query = useQuery({
    queryKey: masterDataKeys.objectRecordsBatch(
      typeIds,
      tenantId,
      perTypeLimit,
      includeArchived
    ),
    queryFn: async () =>
      api.batchListObjectRecords({typeIds, perTypeLimit, includeArchived}),
    enabled: typeIds.length > 0,
  });

  const recordsByType = useMemo<Record<string, ObjectRecord[]>>(() => {
    const batch = query.data ?? {};
    const map: Record<string, ObjectRecord[]> = {};
    for (const typeId of typeIds) {
      map[typeId] = batch[typeId]?.items ?? [];
    }

    return map;
  }, [typeIds, query.data]);

  return {recordsByType, isLoading: query.isLoading, query};
}
